import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { examApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Trash2, Plus, Wand2,
} from 'lucide-react';

const QUESTION_TYPES = ['MCQ', 'Short', 'Long', 'Case Study', 'True/False', 'Fill in the Blank'];
const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];
const BLOOMS = ['Remember', 'Understand', 'Apply', 'Analyse', 'Evaluate', 'Create'];
const BOARDS = ['CBSE', 'ICSE', 'State Board', 'IB', 'IGCSE', 'Other'];
const LANGUAGES = ['English', 'Hindi', 'Kannada', 'Tamil', 'Marathi', 'Bengali'];

const PRESET_BLUEPRINTS = {
  'CBSE 80-mark (5 sections)': [
    { name: 'Section A', count: 20, marks_per_question: 1, types: ['MCQ'], difficulty: 'Easy' },
    { name: 'Section B', count: 5, marks_per_question: 2, types: ['Short'], difficulty: 'Medium' },
    { name: 'Section C', count: 6, marks_per_question: 3, types: ['Short'], difficulty: 'Medium' },
    { name: 'Section D', count: 4, marks_per_question: 5, types: ['Long'], difficulty: 'Hard' },
    { name: 'Section E', count: 3, marks_per_question: 4, types: ['Case Study'], difficulty: 'Hard' },
  ],
  'Unit Test 25-mark': [
    { name: 'Section A', count: 5, marks_per_question: 1, types: ['MCQ'], difficulty: 'Easy' },
    { name: 'Section B', count: 5, marks_per_question: 2, types: ['Short'], difficulty: 'Medium' },
    { name: 'Section C', count: 2, marks_per_question: 5, types: ['Long'], difficulty: 'Hard' },
  ],
  'Periodic Test 40-mark': [
    { name: 'Section A', count: 10, marks_per_question: 1, types: ['MCQ'], difficulty: 'Easy' },
    { name: 'Section B', count: 5, marks_per_question: 2, types: ['Short'], difficulty: 'Medium' },
    { name: 'Section C', count: 4, marks_per_question: 5, types: ['Long'], difficulty: 'Hard' },
  ],
};

const AIGeneratePaperDialog = ({
  open, onOpenChange, classes, subjects, defaultClassId, defaultSubjectId, onApply,
}) => {
  const [syllabusList, setSyllabusList] = useState([]);
  const [form, setForm] = useState({
    class_id: defaultClassId || '',
    subject_id: defaultSubjectId || '',
    board: 'CBSE',
    language: 'English',
    exam_type: 'Unit Test',
    total_marks: 25,
    duration_minutes: 60,
    chapters_selected: [],
    chapters_extra: '',
    sections: PRESET_BLUEPRINTS['Unit Test 25-mark'],
    bloom_targets: ['Remember', 'Understand', 'Apply'],
    extra_prompt: '',
  });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open) return;
    examApi.listSyllabus().then(r => setSyllabusList(r.data.syllabus || []));
    if (defaultClassId) setForm(f => ({ ...f, class_id: defaultClassId }));
    if (defaultSubjectId) setForm(f => ({ ...f, subject_id: defaultSubjectId }));
  }, [open, defaultClassId, defaultSubjectId]);

  const filteredSubjects = useMemo(() => {
    if (!form.class_id) return subjects;
    return subjects.filter(s => s.class_id === form.class_id);
  }, [subjects, form.class_id]);

  const syllabusChapters = useMemo(() => {
    const found = syllabusList.find(s =>
      s.class_id === form.class_id && s.subject_id === form.subject_id);
    return found?.chapters || [];
  }, [syllabusList, form.class_id, form.subject_id]);

  const toggleChapter = (ch) => {
    setForm(f => ({
      ...f,
      chapters_selected: f.chapters_selected.includes(ch)
        ? f.chapters_selected.filter(c => c !== ch)
        : [...f.chapters_selected, ch],
    }));
  };

  const computedTotal = useMemo(() =>
    form.sections.reduce((sum, s) => sum + (s.count * s.marks_per_question), 0),
  [form.sections]);

  const updateSection = (i, patch) => {
    setForm(f => ({
      ...f,
      sections: f.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s),
    }));
  };

  const addSection = () => {
    setForm(f => ({
      ...f,
      sections: [...f.sections, {
        name: `Section ${String.fromCharCode(65 + f.sections.length)}`,
        count: 5, marks_per_question: 2, types: ['Short'], difficulty: 'Medium',
      }],
    }));
  };

  const removeSection = (i) => {
    setForm(f => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));
  };

  const applyPreset = (key) => {
    const sections = PRESET_BLUEPRINTS[key];
    const total = sections.reduce((s, x) => s + x.count * x.marks_per_question, 0);
    setForm(f => ({ ...f, sections, total_marks: total }));
  };

  const generate = async () => {
    const chapters = [
      ...form.chapters_selected,
      ...form.chapters_extra.split('\n').map(s => s.trim()).filter(Boolean),
    ];
    if (!form.class_id || !form.subject_id) { toast.error('Choose class & subject'); return; }
    if (chapters.length === 0) { toast.error('Select at least one chapter'); return; }
    if (form.sections.length === 0) { toast.error('Add at least one section'); return; }
    if (computedTotal !== form.total_marks) {
      toast.error(`Blueprint sums to ${computedTotal} but total marks is ${form.total_marks}. Adjust to match.`);
      return;
    }
    setGenerating(true);
    try {
      const { data } = await examApi.aiGeneratePaper({
        class_id: form.class_id,
        subject_id: form.subject_id,
        chapters,
        exam_type: form.exam_type,
        total_marks: form.total_marks,
        duration_minutes: form.duration_minutes,
        sections_blueprint: form.sections,
        board: form.board,
        language: form.language,
        bloom_targets: form.bloom_targets,
        extra_prompt: form.extra_prompt,
      });
      onApply(data);  // hand the generated paper to CreatePaperPage
      onOpenChange(false);
      toast.success('AI paper generated. Review and save as draft.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI paper generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            AI Question Paper Generator
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>Class *</Label>
              <Select value={form.class_id}
                      onValueChange={(v) => setForm(f => ({ ...f, class_id: v, subject_id: '' }))}>
                <SelectTrigger data-testid="ai-paper-class"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject *</Label>
              <Select value={form.subject_id}
                      onValueChange={(v) => setForm(f => ({ ...f, subject_id: v }))}>
                <SelectTrigger data-testid="ai-paper-subject"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {filteredSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Board</Label>
              <Select value={form.board} onValueChange={(v) => setForm(f => ({ ...f, board: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BOARDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={form.language} onValueChange={(v) => setForm(f => ({ ...f, language: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Exam Type</Label>
              <Input value={form.exam_type}
                     onChange={e => setForm(f => ({ ...f, exam_type: e.target.value }))} />
            </div>
            <div>
              <Label>Total Marks</Label>
              <Input type="number" value={form.total_marks}
                     onChange={e => setForm(f => ({ ...f, total_marks: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label>Duration (mins)</Label>
              <Input type="number" value={form.duration_minutes}
                     onChange={e => setForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label className="text-xs">Computed Total</Label>
              <div className={`h-10 flex items-center px-3 rounded-md border text-sm font-medium ${
                computedTotal === form.total_marks
                  ? 'border-green-300 bg-green-50 text-green-700 dark:bg-green-950/20'
                  : 'border-red-300 bg-red-50 text-red-700 dark:bg-red-950/20'}`}>
                {computedTotal} marks
              </div>
            </div>
          </div>

          {/* Chapters */}
          <div>
            <Label>Chapters from Syllabus</Label>
            {syllabusChapters.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-1">
                No chapters saved for this class+subject. Add chapters in <b>Syllabus</b> page or type below.
              </p>
            ) : (
              <div className="border rounded p-2 max-h-32 overflow-y-auto grid grid-cols-2 gap-1 mt-1">
                {syllabusChapters.map(ch => (
                  <label key={ch} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-muted/50 rounded">
                    <Checkbox checked={form.chapters_selected.includes(ch)}
                              onCheckedChange={() => toggleChapter(ch)} />
                    <span className="truncate">{ch}</span>
                  </label>
                ))}
              </div>
            )}
            <Label className="text-xs mt-2 block">Extra chapters / topics (one per line)</Label>
            <Textarea rows={2} value={form.chapters_extra}
                      onChange={e => setForm(f => ({ ...f, chapters_extra: e.target.value }))} />
          </div>

          {/* Blueprint presets */}
          <div>
            <Label>Section Blueprint</Label>
            <div className="flex flex-wrap gap-2 mb-2 mt-1">
              {Object.keys(PRESET_BLUEPRINTS).map(k => (
                <button key={k} type="button" onClick={() => applyPreset(k)}
                        className="px-3 py-1 rounded-full text-xs border bg-background hover:bg-muted transition">
                  {k}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {form.sections.map((sec, i) => (
                <div key={i} className="border rounded p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                  <div className="md:col-span-3">
                    <Label className="text-xs">Name</Label>
                    <Input value={sec.name} onChange={e => updateSection(i, { name: e.target.value })} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Count</Label>
                    <Input type="number" min="1" value={sec.count}
                           onChange={e => updateSection(i, { count: parseInt(e.target.value) || 1 })} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Marks/Q</Label>
                    <Input type="number" step="0.5" value={sec.marks_per_question}
                           onChange={e => updateSection(i, { marks_per_question: parseFloat(e.target.value) || 1 })} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Type</Label>
                    <Select value={sec.types[0] || 'Short'}
                            onValueChange={(v) => updateSection(i, { types: [v] })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {QUESTION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Difficulty</Label>
                    <Select value={sec.difficulty}
                            onValueChange={(v) => updateSection(i, { difficulty: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DIFFICULTIES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-1">
                    <Button size="sm" variant="ghost" onClick={() => removeSection(i)}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addSection}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Section
              </Button>
            </div>
          </div>

          {/* Blooms */}
          <div>
            <Label>Bloom&apos;s Taxonomy targets</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {BLOOMS.map(b => (
                <button key={b} type="button"
                        onClick={() => setForm(f => ({
                          ...f,
                          bloom_targets: f.bloom_targets.includes(b)
                            ? f.bloom_targets.filter(x => x !== b)
                            : [...f.bloom_targets, b],
                        }))}
                        className={`px-3 py-1 rounded-full text-xs border transition ${
                          form.bloom_targets.includes(b)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background hover:bg-muted'}`}>
                  {b}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Additional guidance (optional)</Label>
            <Textarea rows={2} value={form.extra_prompt}
                      placeholder="e.g. include 2 diagram-based questions in Section C"
                      onChange={e => setForm(f => ({ ...f, extra_prompt: e.target.value }))} />
          </div>

          <div className="bg-muted/50 rounded p-3 text-xs text-muted-foreground">
            <Badge variant="outline" className="mr-2">Note</Badge>
            The AI will generate the paper using your chapters and blueprint. After generation, the questions
            are <b>auto-saved to the Question Bank</b> and the paper opens as a <b>draft</b> for your review.
            You can edit, then Submit for Approval as normal.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={generate} disabled={generating} data-testid="ai-paper-generate-btn">
            {generating
              ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
              : <Sparkles className="h-4 w-4 mr-2" />}
            {generating ? 'Generating…' : 'Generate Paper with AI'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIGeneratePaperDialog;
